import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Flex, Form, Input, Row } from 'antd';
import { useMessageContext } from 'components/Message/CreateMessageComponent/MessageContext';
import ContentPreview from 'components/Message/CreateMessageComponent/StepMessage/ContentPreview/ContentPreview';
import UploadArea from 'components/Message/CreateMessageComponent/StepMessage/ManualCreation/UploadArea/UploadArea';
import { useMessageService } from 'components/Message/useMessageService';
import SpeechAntdTextArea, { SpeechAntdTextAreaHandle } from 'components/UiKit/SpeechAntdTextArea/SpeechAntdTextArea';
import { useEffect, useRef, useState } from 'react';

type AllProps = {
  initContent?: string;
  initTitle?: string;
  onNext: (shouldExit?: boolean) => void;
  onBack: () => void;
  goToDeliveryInfo: () => void;
};

function TextEditor({ initContent, initTitle, onBack, onNext, goToDeliveryInfo }: AllProps) {
  const messageRef = useRef<SpeechAntdTextAreaHandle>(null);
  const { message } = useMessageService();
  const [draftText, setDraftText] = useState<string>(initContent || '');
  const [mode, setMode] = useState<'init' | 'composing' | 'preview'>(initContent ? 'preview' : 'init');
  const { content, file, setContent, setFile } = useMessageContext();
  const [form] = Form.useForm();

  useEffect(() => {
    if (file || content) {
      setMode('preview');
      if (content) {
        setDraftText(content);
        form.setFieldsValue({ content });
      }
    }
  }, [content, file, form]);

  const saveDraft = async () => {
    const finalMessageContent = (await messageRef.current?.flush()) ?? draftText;
    setDraftText(finalMessageContent);

    await form.validateFields();
    const fields = form.getFieldsValue();

    setContent(fields.content);
    setMode('preview');
  };

  if (!message) return null;

  return (
    <div className="step-creation-text-editor create-message-component-full-height">
      {mode === 'preview' ? (
        <ContentPreview
          message={message}
          onNext={onNext}
          onBack={() => setMode(file ? 'init' : 'composing')}
          onRestart={() => {
            setMode('init');
            setDraftText('');
            form.setFieldsValue({ content: '' });
          }}
          goToDeliveryInfo={goToDeliveryInfo}
          file={file}
          text={draftText}
          title={initTitle}
        />
      ) : mode === 'composing' ? (
        <>
          <Form
            layout="vertical"
            onFinish={saveDraft}
            autoComplete="off"
            requiredMark="optional"
            form={form}
            initialValues={{ content: message.content || initContent || '' }}
            className="content-area"
          >
            <div className="create-message-component-subtitle text-left">Message Content</div>

            <Form.Item name="content" rules={[{ required: true }]}>
              <SpeechAntdTextArea
                ref={messageRef}
                autoFocus
                rows={14}
                value={draftText}
                onChange={(value) => {
                  setDraftText(value);
                }}
                placeholder="Type your message here!"
                maxLength={5000}
                showCount={true}
              />
            </Form.Item>
          </Form>

          <Row className="create-message-component-footer">
            <Col span={12}>
              <Button onClick={onBack} type="link">
                <ArrowLeftOutlined /> Back
              </Button>
            </Col>
            <Col span={12} className="text-right">
              <Flex gap={20} justify="flex-end">
                <Button onClick={saveDraft} type="primary">
                  Next
                </Button>
              </Flex>
            </Col>
          </Row>
        </>
      ) : (
        <>
          <div>
            <UploadArea
              mediaConstraintKey="textAttachment"
              onSelectFile={(file) => {
                setFile(file);
                setMode('preview');
              }}
            />

            <Divider plain>or</Divider>

            <Input.TextArea rows={4} placeholder="Type your message here!" onClick={() => setMode('composing')} />
          </div>

          <Row className="create-message-component-footer">
            <Col span={12}>
              <Button onClick={onBack} type="link">
                <ArrowLeftOutlined /> Back
              </Button>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default TextEditor;
